// ============================================================================
// DUE MODI DI ENTRARE, UN SOLO CODICE, UNA SOLA COMPILAZIONE.
//
//  - "clerk"  : Lithosbase sui nostri server. L'identita' la tiene Clerk.
//  - "local"  : Lithosbase installato dal cliente. Il login vive dentro Convex,
//               cioe' dentro la loro macchina, perche' Clerk e' un servizio
//               online e chi compra la licenza vuole tutto in casa.
//
// PERCHE' NON E' UNA COSTANTE
// Il pacchetto che consegniamo al cliente e' un'immagine gia' compilata: non
// sappiamo, mentre la costruiamo, a quale indirizzo risponderanno il suo server
// e il suo database. Se queste due cose finissero dentro il codice al momento
// della compilazione servirebbe una compilazione per cliente. Quindi si leggono
// all'accensione: il server le prende dalle variabili d'ambiente e le passa al
// browser dentro la pagina (vedi RuntimeConfigScript nel layout).
//
// Lato Convex esiste la variabile gemella AUTH_MODE, letta da
// convex/auth.config.ts. Le due devono dire la stessa cosa: se non concordano,
// i token firmati da una parte non vengono accettati dall'altra.
// ============================================================================

export type AuthMode = 'clerk' | 'local';

/**
 * Verdetto sulla licenza, senza la firma: al browser serve sapere di chi e'
 * l'installazione e se il servizio e' in corso, non come si dimostra.
 * Il controllo vero sta in src/lib/licence.ts, lato server.
 */
export type LicenceSummary = {
  status: 'none' | 'invalid' | 'active' | 'lapsed';
  customer: string | null;
  serviceUntil: number | null;
};

/** Quello che il server passa al browser all'accensione. */
export type RuntimeConfig = {
  authMode: AuthMode;
  convexUrl: string;
  licence: LicenceSummary;
  /** True se questa installazione sa spedire posta (Resend oppure SMTP). */
  mailEnabled: boolean;
};

/** Nome della variabile globale in cui la pagina porta la configurazione. */
export const RUNTIME_CONFIG_KEY = '__LITHOSBASE_CONFIG__';

declare global {
  interface Window {
    [RUNTIME_CONFIG_KEY]?: RuntimeConfig;
  }
}

function normalise(value: string | undefined): AuthMode {
  return value === 'local' ? 'local' : 'clerk';
}

/**
 * Configurazione letta dalle variabili d'ambiente. Vale solo lato server:
 * nel browser process.env contiene soltanto le NEXT_PUBLIC_*, che sono quelle
 * congelate al momento della compilazione.
 */
const NO_LICENCE: LicenceSummary = { status: 'none', customer: null, serviceUntil: null };

export function serverRuntimeConfig(licence: LicenceSummary = NO_LICENCE): RuntimeConfig {
  return {
    authMode: normalise(process.env.AUTH_MODE ?? process.env.NEXT_PUBLIC_AUTH_MODE),
    convexUrl: process.env.CONVEX_URL ?? process.env.NEXT_PUBLIC_CONVEX_URL ?? '',
    licence,
    mailEnabled: Boolean(process.env.SMTP_HOST || process.env.RESEND_API_KEY),
  };
}

/**
 * Indirizzo del backend Convex per il codice che gira sul SERVER.
 *
 * Nel cloud e' lo stesso di `convexUrl` e questa funzione non serve a niente.
 * Serve nell'installazione del cliente, dove i due indirizzi sono per forza
 * diversi: il browser deve chiamare la macchina sulla rete dell'azienda
 * (per esempio http://192.168.1.50:3210), mentre l'app, che gira dentro Docker,
 * raggiunge il database come `http://backend:3210`. Se il server usa quello
 * pubblico cerca se stesso e trova la porta chiusa: la registrazione moriva
 * cosi', con connessione rifiutata (prova d'uso del 2026-09-04).
 *
 * Senza `CONVEX_SERVER_URL` il comportamento resta quello di prima.
 */
export function serverConvexUrl(): string {
  return process.env.CONVEX_SERVER_URL ?? serverRuntimeConfig().convexUrl;
}

/**
 * Configurazione valida ovunque. Nel browser viene dalla pagina; se manca
 * (pagina servita da una versione vecchia, o test) si ripiega sui valori
 * congelati in compilazione, che sono quelli giusti per il cloud.
 */
export function runtimeConfig(): RuntimeConfig {
  if (typeof window !== 'undefined') {
    const injected = window[RUNTIME_CONFIG_KEY];
    if (injected) return injected;
    return {
      authMode: normalise(process.env.NEXT_PUBLIC_AUTH_MODE),
      convexUrl: process.env.NEXT_PUBLIC_CONVEX_URL ?? '',
      licence: NO_LICENCE,
      mailEnabled: true,
    };
  }
  return serverRuntimeConfig();
}

/** Vero quando il login e' interno, cioe' nell'installazione del cliente. */
export function isLocalAuth(): boolean {
  return runtimeConfig().authMode === 'local';
}

/** Indirizzo del backend Convex da usare adesso. */
export function convexUrl(): string {
  return runtimeConfig().convexUrl;
}

/** Licenza di questa installazione, come la vede il browser. */
export function licenceSummary(): LicenceSummary {
  return runtimeConfig().licence;
}

/** True se ha senso provare a spedire una mail da questa installazione. */
export function mailEnabled(): boolean {
  return runtimeConfig().mailEnabled;
}
